import styled from '@emotion/styled';
import { Fade, ListItemText, Menu, MenuItem, Stack, Typography } from '@mui/material';
import { GridPaginationModel } from '@mui/x-data-grid';
import { AxiosResponse } from 'axios';
import { concat } from 'lodash';
import debounce from 'lodash/debounce';
import { ManageTasksEvent } from 'klayowebshared';
import React, { SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { track } from '../../common/track';
import { Button } from '../../components/Button';
import { Dialog } from '../../components/dialogs/Dialog';
import { Snackbar } from '../../components/Snackbar';
import StandardLibraryCheck from '../../components/StandardLibraryCheck';
import { StandardLibraryTable } from '../../components/table/StandardLibraryTable';
import { TextField } from '../../components/TextField';
import { useAppMutation } from '../../hooks/useAppMutation';
import { useAppQuery } from '../../hooks/useAppQuery';
import { useLocale } from '../../providers/LocaleProvider';
import {
  ITask,
  ITaskCompetencies,
  ITaskCompetenciesRequest,
  ITaskCompetency,
  ITaskImportResponse,
  ITaskListResponse,
} from '../../interfaces/Task';
import ChevronDownWhiteIcon from '../../resources/images/icons-chevron-down-white.svg';
import RemoveIcons from '../../resources/images/icons-remove.svg';
import SearchIcon from '../../resources/images/icons-search.svg';
import { KLAYO_COLORS } from '../../themes';
import ImportTaskDialog from './ImportTaskDetailDialog';
import ImportTaskListConfirmDialog from './ImportTaskListConfirmDialog';
import ImportTaskListDialog from './ImportTaskListDialog';
import ImportTasksErrorDialog from './ImportTasksErrorDialog';
import LimitedOverlay from './LimitedOverlay';

export const ImportTaskFromIndustryStandardDialog = ({
  theme,
  onClose,
  organization,
  open,
}: any) => {
  const { translate } = useLocale();
  const isTrial = !!organization?.isTrial || !organization?.enabledFeatures?.KlayoIntelligence;
  const [pagingModel, setPagingModel] = useState<GridPaginationModel>({ page: 0, pageSize: 5 });
  const [queryText, setQueryText] = useState<string>('');
  const [inputValue, setInputValue] = useState<string>('');
  const [importMenuAnchorEl, setImportMenuAnchorEl] = useState<any>(null);
  const [importMenuOpen, setImportMenuOpen] = useState<boolean>(false);
  const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
  const [showImportCompetencies, setShowImportCompetencies] = useState(false);
  const [showImportTaskDuplicatedDialog, setShowImportTaskDuplicatedDialog] = useState(false);
  const [showImportTaskListConfirmDialog, setShowImportTaskListConfirmDialog] = useState(false);
  const [competencies, setCompetencies] = useState<ITaskCompetency[]>([]);
  const [cacheRows, setCacheRows] = useState<ITaskImportResponse>({
    importedTasks: [],
    notImportedTasks: [],
    failedToImportTasks: [],
  });
  const [showMessage, setShowMessage] = useState(false);
  const [showImportTasksError, setShowImportTasksError] = useState(false);

  const { data: similarJob, isFetching } = useAppQuery<ITaskListResponse>(
    `/StandardLibrary/tasks`,
    {
      config: {
        params: {
          pageNumber: pagingModel.page + 1,
          pageSize: pagingModel.pageSize,
          searchText: (queryText || '').trim(),
          showExtra: true,
        },
      },
      placeholderData: undefined,
      enabled: organization?.enableStandardsLibrary,
    }
  );

  const importCompetenciesMutation = useAppMutation<
    AxiosResponse<ITaskImportResponse>,
    unknown,
    string[]
  >('post', '/StandardLibrary/tasks/import', {
    onSuccess: (data) => {
      setShowImportCompetencies(false);
      setShowImportTaskListConfirmDialog(false);
      setShowImportTaskDuplicatedDialog(false);
      setCacheRows(data.data);
      setShowMessage(true);
    },
    onError: () => {},
  });

  const taskListingCompetencies = useAppMutation<
    AxiosResponse<ITaskCompetencies>,
    unknown,
    ITaskCompetenciesRequest
  >('post', '/StandardLibrary/tasks/competencies', {
    onSuccess: (data) => {
      setShowImportTaskDuplicatedDialog(false);
      setShowImportCompetencies(false);

      if (data.data.competencies.length > 0) {
        setShowImportTaskListConfirmDialog(true);
        setCompetencies(data.data.competencies);
      } else {
        setShowImportTaskListConfirmDialog(false);
        onImportTaskList();
      }
    },
  });

  const handlePaginationModelChange = (newModel: GridPaginationModel) => {
    setPagingModel(newModel);
    setSelectedRowId(null);
  };

  const onOpenDetail = () => {
    track(ManageTasksEvent.NewTaskIntelligenceViewDetail);
    setImportMenuOpen(false);
    setShowImportCompetencies(true);
  };

  const onSetShowImportTaskDuplicatedDialog = useCallback((isOpen: boolean) => {
    setShowImportTaskDuplicatedDialog(isOpen);
  }, []);

  const debouncedSetQueryText = useCallback(
    debounce((value: string) => {
      if (value) track(ManageTasksEvent.NewTaskIntelligenceSearch, { search: value });
      setQueryText(value);
      setPagingModel((prev) => ({ page: 0, pageSize: prev.pageSize }));
      setSelectedRowId(null);
    }, 500),
    []
  );

  const handleSearchChange = useCallback(
    (e: any) => {
      setInputValue(e.target.value);
      debouncedSetQueryText(e.target.value);
    },
    [debouncedSetQueryText]
  );

  const isLimitedAccess = useMemo(() => {
    if (!similarJob || !isTrial) return false;

    const tasks = similarJob?.standardLibraryTasks?.data;
    return tasks.some((t) => t.isExtra);
  }, [similarJob, isTrial]);

  const similarJobs = useMemo(() => {
    if (!similarJob) return [];

    return similarJob?.standardLibraryTasks.data || [];
  }, [similarJob]);

  const extraTasks = useMemo(() => similarJobs.filter((t) => t.isExtra), [similarJobs]);

  const onCloseImportTask = () => {
    setShowImportCompetencies(false);
  };

  const onImportSingleCompetency = (competencyId: string) => {
    if (competencyId) {
      importCompetenciesMutation.mutate([competencyId]);
    }
  };

  const onOpenImportDialog = () => {
    setShowImportCompetencies(true);
    setImportMenuOpen(false);
  };

  const handleActionMenu = (event: SyntheticEvent, row: any) => {
    setImportMenuAnchorEl(event.currentTarget);
    setImportMenuOpen(true);
  };

  const setStateOfMessage = (state: boolean) => {
    setShowMessage(state);
  };

  const onCloseImportTaskListConfirmDialog = useCallback(() => {
    setShowImportTaskListConfirmDialog(false);
  }, []);

  const onImportTaskList = useCallback(() => {
    if (selectedRowId) {
      track(ManageTasksEvent.NewTaskIntelligenceImport);
      importCompetenciesMutation.mutate([selectedRowId]);
    }
  }, [selectedRowId]);

  const onCheckCompetencies = useCallback(() => {
    if (selectedRowId) {
      taskListingCompetencies.mutate({
        taskIds: [selectedRowId],
      });
    }
  }, [selectedRowId]);

  const onImportTasksError = useCallback(() => {
    setShowImportTasksError(true);
    setStateOfMessage(false);
  }, []);

  const onCloseImportTasksError = useCallback(() => {
    setShowImportTasksError(false);
  }, []);

  const errorTasks = useMemo(
    () => concat(cacheRows.notImportedTasks, cacheRows.failedToImportTasks),
    [cacheRows.notImportedTasks, cacheRows.failedToImportTasks]
  );

  const hasCompetencies = useCallback((tasks: ITask[]) => {
    if (tasks.length > 1) {
      return tasks.some((task) => task.numberOfCompetencies);
    }
    return Boolean(tasks.find((task) => task.numberOfCompetencies));
  }, []);

  const currentRowTask = useMemo(() => {
    return selectedRowId ? similarJobs.filter((row) => row.id === selectedRowId) : [];
  }, [similarJobs, selectedRowId]);

  const element = document.querySelector('#table-container-popup div.MuiDataGrid-main');
  return (
    <StyledImportJobFromIndustryStandardDialog
      className='klayo__import-job-from-industry-standard-dialog'
      open={open}
      theme={theme}
      onClose={onClose}
      fullWidth
      maxWidth='sm'
    >
      <h1 className='truncate'>{translate('settings.klayoIntelligenceTasks')}</h1>
      <StandardLibraryCheck>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='start'
          spacing={2}
          paddingBottom={2}
        >
          <div style={{ flex: 2 }}>
            <TextField
              dense={true}
              placeholder={translate('placeholders.searchTask')}
              autoComplete={false}
              leadingIcon={<SearchIcon />}
              fullWidth={true}
              onChange={handleSearchChange}
              value={inputValue}
            />
          </div>
        </Stack>

        <StyledStandardLibraryTable id='table-container-popup'>
          <StandardLibraryTable
            totalCount={similarJob?.standardLibraryTasks?.totalCount}
            rowHasAction={true}
            dense={true}
            rowHeight={44}
            paper={false}
            sortingMode='server'
            filterMode='server'
            queryText={queryText}
            theme={theme}
            showHeader={false}
            isLoading={isFetching}
            translate={translate}
            columns={[
              { type: StandardLibraryTable.columns.nameSuggestion },
              { type: StandardLibraryTable.columns.competenciesName },
            ]}
            paginationModel={pagingModel}
            onPaginationModelChange={handlePaginationModelChange}
            paginationMode='server'
            rows={similarJobs ? similarJobs : null}
            rowSelectionModel={selectedRowId ? [selectedRowId] : []}
            onJobSelect={(_event: any, row: any) => {
              if (row.isExtra) return;

              const next = selectedRowId === row.id ? null : row.id;
              if (next !== null) track(ManageTasksEvent.NewTaskIntelligenceSelect);
              setSelectedRowId(next);
            }}
            onActionMenu={handleActionMenu}
            sx={{
              '& .MuiDataGrid-selectedRowCount': {
                display: 'none',
              },
              '& .MuiTablePagination-root': {
                width: '100%',
              },
            }}
            showFooter={false}
            hideFirstLastBorder
          />
        </StyledStandardLibraryTable>
      </StandardLibraryCheck>
      <div className='button-container'>
        <div className='button-container-left'>
          <Button
            size='md'
            theme={theme}
            label={
              organization?.enableStandardsLibrary
                ? translate('common.cancel')
                : translate('common.close')
            }
            onClick={onClose}
          />
        </div>
        {organization?.enableStandardsLibrary && (
          <div className='klaro_regulations-button-container'>
            <Button
              size='md'
              theme={theme}
              variant='filled'
              label={
                <p style={{ fontSize: 14, padding: '0 .3rem' }}>{translate('common.import')}</p>
              }
              className='klaro_regulations-button-add-regulation'
              onClick={() => onSetShowImportTaskDuplicatedDialog(true)}
              disabled={!selectedRowId}
            />
            <div className='klaro_regulations-vertical-line'></div>
            <div
              className='klaro_regulations-domain-category'
              data-disabled={!selectedRowId}
              style={{
                cursor: !selectedRowId ? 'not-allowed' : 'pointer',
                opacity: !selectedRowId ? 0.5 : 1,
                pointerEvents: !selectedRowId ? 'none' : 'auto',
              }}
            >
              <ChevronDownWhiteIcon
                onClick={(event: any) => {
                  if (!selectedRowId) return;
                  setImportMenuAnchorEl(event.currentTarget);
                  setImportMenuOpen(true);
                }}
                style={{
                  cursor: !selectedRowId ? 'not-allowed' : 'pointer',
                  opacity: !selectedRowId ? 0.5 : 1,
                }}
              />
            </div>
          </div>
        )}
      </div>

      <Menu
        open={importMenuOpen}
        anchorEl={importMenuAnchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        onClose={() => setImportMenuOpen(false)}
      >
        <MenuItem onClick={onOpenDetail}>
          <ListItemText>{translate('settingsViews.tasks.importTasks.viewDetail')}</ListItemText>
        </MenuItem>
      </Menu>

      <Fade in={showMessage} timeout={300} unmountOnExit>
        <div>
          <Snackbar
            open={true}
            autoHideDuration={10000}
            type={errorTasks.length > 0 ? 'error' : 'success'}
            onClose={() => setStateOfMessage(false)}
            message={
              <React.Fragment>
                <Typography hidden={errorTasks.length === 0}>
                  {translate('settings.tasksNotImported', {
                    count: errorTasks.length,
                    taskWord:
                      errorTasks.length <= 1
                        ? translate('entities.task').toLowerCase()
                        : translate('entities.tasks').toLowerCase(),
                  })}
                </Typography>
                <Typography hidden={!(errorTasks.length === 0)}>
                  {translate('settings.tasksSuccessfullyImported', {
                    count: cacheRows.importedTasks.length,
                    taskWord:
                      cacheRows.importedTasks.length <= 1
                        ? translate('entities.task').toLowerCase()
                        : translate('entities.tasks').toLowerCase(),
                  })}
                </Typography>
              </React.Fragment>
            }
            action={
              <React.Fragment>
                <div
                  style={{
                    marginLeft: 'auto',
                    marginRight: '0.5rem',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                  }}
                  hidden={errorTasks.length === 0}
                  className='klayo-snackbar_text'
                  onClick={onImportTasksError}
                >
                  {translate('common.viewDetails')}
                </div>
                <BlackIconContainer
                  style={{
                    padding: '0',
                    cursor: 'pointer',
                    fontWeight: 'bold',
                  }}
                  onClick={() => {
                    setStateOfMessage(false);
                  }}
                  className='klayo-snackbar_text '
                >
                  <RemoveIcons />
                </BlackIconContainer>
              </React.Fragment>
            }
          />
        </div>
      </Fade>

      <ImportTaskListDialog
        theme={theme}
        onClose={() => onSetShowImportTaskDuplicatedDialog(false)}
        open={showImportTaskDuplicatedDialog}
        isLoading={taskListingCompetencies.isPending}
        tasks={currentRowTask}
        hasCompetencies={hasCompetencies(currentRowTask)}
        onImport={() => {
          hasCompetencies(currentRowTask) ? onCheckCompetencies() : onImportTaskList();
        }}
      />

      <ImportTaskListConfirmDialog
        theme={theme}
        onClose={onCloseImportTaskListConfirmDialog}
        open={showImportTaskListConfirmDialog}
        tasks={competencies}
        onImport={onImportTaskList}
        isLoading={importCompetenciesMutation.isPending}
      />

      <ImportTasksErrorDialog
        theme={theme}
        open={showImportTasksError}
        onClose={onCloseImportTasksError}
        tasks={errorTasks}
      />

      <Fade in={showImportCompetencies} timeout={300} unmountOnExit>
        <div>
          <ImportTaskDialog
            theme={theme}
            taskId={selectedRowId}
            onClose={onCloseImportTask}
            open={showImportCompetencies}
            onImport={onCheckCompetencies}
            isImporting={taskListingCompetencies.isPending}
          />
        </div>
      </Fade>

      {isLimitedAccess && (
        <LimitedOverlay element={element} height={extraTasks.length * 44} translate={translate} />
      )}
    </StyledImportJobFromIndustryStandardDialog>
  );
};

const StyledImportJobFromIndustryStandardDialog = styled(Dialog)`
  &.klayo__import-job-from-industry-standard-dialog {
    .klaro_regulations-button-container:has(button[disabled]) {
      opacity: 0.5;
      cursor: not-allowed;
      background-color: ${KLAYO_COLORS.GreyGR200} !important;
    }

    h1 {
      color: ${KLAYO_COLORS.GreyGR900};
      font-family: Inter;
      font-size: 24px;
      font-style: normal;
      font-weight: 600;
      line-height: 32px; /* 133.333% */
      letter-spacing: -0.5px;
      margin-top: 0px;
      margin-bottom: 32px;
    }
    .button-container {
      display: flex;
      justify-content: flex-end;
      padding-bottom: 30px;
      padding-top: 30px;
      gap: 10px;
    }
    h2 {
      color: ${KLAYO_COLORS.GreyGR900};
      font-family: Inter;
      font-size: 14px;
      font-style: normal;
      font-weight: 400;
      line-height: 20px;
      margin-top: 0px;
      margin-bottom: 24px;
    }

    .main-title {
      font-weight: 700;
    }

    .MuiDialogContent-root {
      padding: 40px 40px 0px 40px;
    }

    .MuiPaper-root {
      max-width: 800px;
    }

    .MuiDataGrid-cell {
      border-bottom: none !important;
    }

    .MuiDataGrid-footerContainer {
      border-top: none !important;
    }

    .MuiDataGrid-row {
      border-radius: 7px !important;

      &[aria-selected='true'] {
        background-color: ${KLAYO_COLORS.NeonBlueNB500} !important;

        & > *,
        & .MuiTypography-root {
          color: ${KLAYO_COLORS.WhiteWH900} !important;
        }
      }
    }

    .MuiDataGrid-selectedRowCount {
      visibility: hidden !important;
    }

    .MuiDataGrid-cell:has(p[data-style-id='flex-end']) {
      justify-content: flex-end !important;
    }
  }
`;

const StyledStandardLibraryTable = styled.div`
  .MuiDataGrid-columnHeader:focus-within {
    outline: none !important;
  }
`;

const BlackIconContainer = styled.div`
  svg {
    path[fill='#1B3DF2'] {
      fill: #000;
    }
  }
`;
